import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Modal, Input, InputNumber, DatePicker, Checkbox, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useD205Mode } from './hooks/useD205Mode';
import { useD205Record } from './hooks/useD205Record';
import { useD205Navigation } from './hooks/useD205Navigation';
import { useModulePermission } from '../../hooks/usePermission';
import LookupInput from '../../components/LookupInput';
import { SharedActionBar, ApprovalState } from '../../components/SharedActionBar';
import { d205Api } from '../../services/d205Api';
import type { FD151M } from '../../services/d205Api';
import './D205Form.css';

const { TextArea } = Input;

const D205Form: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get('review');
  const { mode, setMode, isEditing, enterAdd, enterEdit, enterBrowse } = useD205Mode();
  const permissions = useModulePermission('pwd205f1');

  const {
    record,
    formData,
    details,
    loading,
    loadRecord,
    getField,
    setField,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
  } = useD205Record(setMode);

  const loadRecordWithNav = useCallback(
    async (id: string) => {
      await loadRecord(id);
    },
    [loadRecord]
  );

  const { allIds, currentIdx, setCurrentIdx, loadList, navFirst, navPrev, navNext, navLast } =
    useD205Navigation(loadRecordWithNav);

  useEffect(() => {
    loadList().then((ids) => {
      if (reviewId) {
        const idx = ids.indexOf(reviewId);
        if (idx >= 0) setCurrentIdx(idx);
        loadRecord(reviewId);
      } else if (ids.length > 0) {
        setCurrentIdx(0);
        loadRecord(ids[0]);
      }
    });
  }, [loadList, loadRecord, setCurrentIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddNew = () => {
    resetForAdd();
    enterAdd();
  };

  const handleEdit = () => {
    if (!record) { message.warning('請先選取一筆資料'); return; }
    if (record.fd150_80 === 'P') { message.warning('已核准的單據不可修改'); return; }
    enterEdit();
  };

  const handleRevert = () => {
    revertToRecord();
  };

  const handleSaveClick = async () => {
    const ok = await handleSave(mode);
    if (ok) {
      await loadList();
    }
  };

  const handleDeleteClick = () => {
    if (!record) return;
    if (record.fd150_80 === 'P') { message.warning('已核准的單據不可刪除'); return; }
    Modal.confirm({
      title: '確認刪除',
      content: `確認刪除領用單 ${record.fd150_01}？`,
      okText: '刪除',
      cancelText: '取消',
      okType: 'danger',
      onOk: async () => {
        const ok = await handleDelete(record.fd150_01);
        if (ok) {
          const newIds = await loadList();
          if (newIds.length > 0) {
            setCurrentIdx(0);
            loadRecord(newIds[0]);
          }
        }
      },
    });
  };

  // -- Approval Handlers --
  const handleSubmitApproval = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認送審',
      content: `確定要將領用單 ${record.fd150_01} 送審？`,
      okText: '送審',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d205Api.submitForApproval(record.fd150_01);
          message.success(resp.message || '送審成功');
          await loadRecord(record.fd150_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '送審失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleApproveAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認核准',
      content: `確定要核准領用單 ${record.fd150_01}？`,
      okText: '核准',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d205Api.approve(record.fd150_01);
          message.success(resp.message || '核准成功');
          await loadRecord(record.fd150_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '核准失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleRejectAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認退回',
      content: `確定要退回領用單 ${record.fd150_01}？`,
      okText: '退回',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d205Api.reject(record.fd150_01);
          message.success(resp.message || '退回成功');
          await loadRecord(record.fd150_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '退回失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const handleWriteoffAction = useCallback(() => {
    if (!record || isEditing) return;
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷此單據 ${record.fd150_01}？沖銷後可重新送審。`,
      okText: '沖銷',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const resp = await d205Api.writeoff(record.fd150_01);
          message.success(resp.message || '沖銷成功');
          await loadRecord(record.fd150_01);
        } catch (err: unknown) {
          message.error((err as any)?.response?.data?.error || (err instanceof Error ? err.message : '沖銷失敗'));
        }
      },
    });
  }, [record, isEditing, loadRecord]);

  const [showHistory, setShowHistory] = useState(false);

  const isApproved = record?.fd150_80 === 'P';

  const detailColumns: ColumnsType<FD151M> = [
    {
      title: '項次',
      dataIndex: 'fd151_02',
      width: 50,
    },
    {
      title: '產品代號(前段)',
      dataIndex: 'fd151_10',
      width: 120,
      render: (v: string, _: FD151M, idx: number) =>
        isEditing ? (
          <Input
            value={v}
            onChange={(e) => updateDetailField(idx, 'fd151_10', e.target.value)}
            size="small"
          />
        ) : (
          v
        ),
    },
    {
      title: '產品代號(後段)',
      dataIndex: 'fd151_11',
      width: 120,
      render: (v: string, _: FD151M, idx: number) =>
        isEditing ? (
          <Input
            value={v}
            onChange={(e) => updateDetailField(idx, 'fd151_11', e.target.value)}
            size="small"
          />
        ) : (
          v
        ),
    },
    {
      title: '批號',
      dataIndex: 'fd151_12',
      width: 160,
      render: (v: string, _: FD151M, idx: number) =>
        isEditing ? (
          <Input
            value={v}
            onChange={(e) => updateDetailField(idx, 'fd151_12', e.target.value)}
            size="small"
          />
        ) : (
          v
        ),
    },
    {
      title: '等級',
      dataIndex: 'fd151_13',
      width: 60,
    },
    {
      title: '單位',
      dataIndex: 'fd151_14',
      width: 60,
    },
    {
      title: '單位',
      dataIndex: 'fd151_15',
      width: 90,
      render: (v: number) => v?.toLocaleString('en-US', { maximumFractionDigits: 3 }),
    },
    {
      title: '單價',
      dataIndex: 'fd151_16',
      width: 100,
      render: (v: number, _: FD151M, idx: number) =>
        isEditing ? (
          <InputNumber
            value={v}
            onChange={(val) => updateDetailField(idx, 'fd151_16', val ?? 0)}
            size="small"
            style={{ width: '100%' }}
          />
        ) : (
          v?.toLocaleString('en-US', { maximumFractionDigits: 2 })
        ),
    },
    {
      title: '換算率',
      dataIndex: 'fd151_21',
      width: 80,
      render: (v: number, _: FD151M, idx: number) =>
        isEditing ? (
          <InputNumber
            value={v}
            onChange={(val) => updateDetailField(idx, 'fd151_21', val ?? 1)}
            size="small"
            style={{ width: '100%' }}
          />
        ) : (
          v
        ),
    },
    {
      title: '領用數量',
      dataIndex: 'fd151_17',
      width: 100,
      render: (v: number) => v?.toLocaleString('en-US', { maximumFractionDigits: 2 }),
    },
    ...(isEditing
      ? [
          {
            title: '操作',
            width: 60,
            render: (_: unknown, __: FD151M, idx: number) => (
              <Button size="small" danger onClick={() => deleteDetailRow(idx)}>
                刪除
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="d205-form">
      {/* Page wrapper */}
      <div className="d205-page-wrapper">
        {/* Header */}
        <div className="d205-header">
          <h2>
            領用單
            {isApproved && <span className="d205-approved-badge">已核准</span>}
          </h2>
        </div>

        {/* Master fields */}
        <div className="d205-master-grid">
          <label>領用單號</label>
          <Input value={getField('fd150_01')} disabled className="d205-readonly" />

          <label>託工單號</label>
          <Input
            value={getField('fd150_11')}
            onChange={(e) => setField('fd150_11', e.target.value)}
            disabled={!isEditing}
          />

          <label>領用日期</label>
          <DatePicker
            value={getField('fd150_12') ? dayjs(getField('fd150_12')) : null}
            onChange={(d) => setField('fd150_12', d ? d.toISOString() : null)}
            disabled={!isEditing}
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
          />

          <label>領用庫位</label>
          <LookupInput
            lookupType="warehouse"
            value={getField('fd150_13') || ''}
            onChange={(v) => setField('fd150_13', v)}
            onCodeConfirm={(v) => setField('fd150_13', v)}
            disabled={!isEditing}
            maxLength={5}
          />

          <label>收布編號</label>
          <Input
            value={getField('fd150_14')}
            onChange={(e) => setField('fd150_14', e.target.value)}
            disabled={!isEditing}
          />

          <label>加工廠代號</label>
          <Input value={getField('fd150_10')} disabled className="d205-readonly" />

          <label>總領用量</label>
          <Input value={getField('fd150_18')?.toLocaleString('en-US', { maximumFractionDigits: 2 })} disabled className="d205-readonly" />

          <label>總領用金額</label>
          <Input value={getField('fd150_21')?.toLocaleString('en-US', { maximumFractionDigits: 2 })} disabled className="d205-readonly" />

          <label>總項數</label>
          <Input value={getField('fd150_22')} disabled className="d205-readonly" />

          <label>重修再入庫</label>
          <Checkbox
            checked={getField('fd150_26')}
            onChange={(e) => setField('fd150_26', e.target.checked)}
            disabled={!isEditing}
          />

          <label>加工說明</label>
          <TextArea
            value={getField('fd150_15')}
            disabled
            rows={3}
            className="d205-readonly"
            style={{ gridColumn: '2 / span 3' }}
          />
        </div>

        {/* Detail Grid */}
        <div className="d205-detail-section">
          <div className="d205-detail-header">
            <span>領用明細</span>
            {isEditing && (
              <Button size="small" onClick={addDetailRow} disabled={details.length >= 99}>
                明細新增
              </Button>
            )}
          </div>
          <Table<FD151M>
            dataSource={details}
            columns={detailColumns}
            rowKey={(_, idx) => String(idx)}
            size="small"
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>

      {/* Bottom Action Bar (SharedActionBar) */}
      {(() => {
        const s80 = (record?.fd150_80 || '').trim();
        const approval: ApprovalState = {
          statusCode: s80,
          isApproved: s80 === 'P',
          isPending: ['D', 'S'].includes(s80), // 統一碼 D + 舊碼 S 過渡容忍
          isLocked: ['P', 'D', 'S'].includes(s80),
        };
        return (
          <SharedActionBar
            mode={mode} isEditing={isEditing} hasRecord={!!record}
            currentIdx={currentIdx} recordCount={allIds.length}
            onNavFirst={navFirst} onNavPrev={navPrev}
            onNavNext={navNext} onNavLast={navLast}
            onAdd={handleAddNew} onModify={handleEdit} onDelete={handleDeleteClick}
            onSave={handleSaveClick} onCancel={handleRevert}
            approval={approval}
            onSubmit={handleSubmitApproval} onApprove={handleApproveAction}
            onReject={handleRejectAction} onWriteoff={handleWriteoffAction} onHistory={() => setShowHistory(true)}
            onExit={() => navigate('/erp')}
            canCreate={permissions.canCreate} canEdit={permissions.canEdit}
            canDelete={permissions.canDelete} canApprove={permissions.canApprove}
            canPrint={permissions.canPrint}
          />
        );
      })()}
    </div>
  );
};

export default D205Form;
